// routesConfig.js - Complete routes configuration with Jewellery module components

import { lazy } from "react";
import NotFound from "../pages/NotFound";
import Login from "../pages/Login";
import InitialSetupWizard from "pages/InitialSetup";
import Layout from "../components/layout/CollapsableLayout";
import ProtectedRoute from "../components/common/ProtectedRoute";
// import Orders from "../pages/NewOrders";
import CategoryPage from "../pages/Category";
import AdminProductPage from "../pages/AdminProduct/AdminProductPage";
import UsersPage from "../pages/User";
import AdminPage from "../pages/Admin";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import UserRequestsPage from "../pages/UserRequests";
import UsageDashboard from "../pages/Usage";
import CompanyDetailsForm from "../components/UI/CompanyDetailsForm";

// Notification Pages
import NotificationsDashboard from "../pages/Notifications/Dashboard/NotificationsDashboard";
import TemplateManager from "../pages/Notifications/Templates/TemplateManager";
import CampaignWizard from "../pages/Notifications/Campaigns/CampaignWizard";
import JobsPage from "../pages/Notifications/Jobs/JobsPage";
import DeviceRegistry from "../pages/Notifications/Devices/DeviceRegistry";
import AnalyticsPage from "../pages/Notifications/Analytics/AnalyticsPage";

// ==================== JEWELLERY MODULE IMPORTS ====================
// Dashboard
import PlansDashboard from "../pages/JewelleryPlans/Dashboard/PlansDashboard";

// Plan Management
import CreatePlanWizard from "../pages/JewelleryPlans/Plans/CreatePlan/CreatePlanWizard";
import PlanDetailsPage from "../pages/JewelleryPlans/Plans/PlanDetails/PlanDetailsPage";
import ActivePlans from "../pages/JewelleryPlans/Plans/PlansList/ActivePlans";
import MaturedPlans from "../pages/JewelleryPlans/Plans/PlansList/MaturedPlans";

// Payments
import PaymentsList from "../pages/JewelleryPlans/Payments/PaymentsList";
import PaymentForm from "../pages/JewelleryPlans/Payments/RecordPayment/PaymentForm";
import PendingVerifications from "../pages/JewelleryPlans/Payments/VerifyPayments/PendingVerifications";
import PaymentDetails from "../pages/JewelleryPlans/Payments/PaymentDetails";

// Redemptions
import RedemptionList from "../pages/JewelleryPlans/Redemptions/RedemptionList";
import RedemptionDetails from "../pages/JewelleryPlans/Redemptions/RedemptionDetails";
import BalanceCheck from "../pages/JewelleryPlans/Redemptions/BalanceCheck";
import QuoteDetails from "../pages/JewelleryPlans/Redemptions/QuoteDetails";
import PlanQuotes from "../pages/JewelleryPlans/Redemptions/PlanQuotes";

// Cancellations - NEW IMPORTS
import CancellationWizard from "../pages/JewelleryPlans/Cancellation/CancellationWizard";
import CancellationStatus from "../pages/JewelleryPlans/Cancellation/CancellationStatus";

// Admin
import JewelleryTemplateManager from "../pages/JewelleryPlans/Admin/Templates/TemplateManager";

// Reports
import ReportsHub from "../pages/JewelleryPlans/Reports/ReportsHub";
import PendingVerificationsReport from "../pages/JewelleryPlans/Reports/PendingVerifications";
import OverdueReport from "../pages/JewelleryPlans/Reports/OverdueReport";
import MaturityReport from "../pages/JewelleryPlans/Reports/MaturityReport";
import RedemptionAnalytics from "../pages/JewelleryPlans/Reports/RedemptionAnalytics";

import RateDashboard from "../pages/Rates/Dashboard/RateDashboard";
import DailyRateManager from "../pages/Rates/DailyRates/DailyRateManager";
import BranchRateConfig from "../pages/Rates/BranchConfig/BranchRateConfig";
import StoneRateMatrix from "../pages/Rates/Stones/StoneRateMatrix";
import RateAnalytics from "../pages/Rates/Analytics/RateAnalytics";
import ChargesManager from "../pages/Rates/Charges/ChargesManager";

import BranchManagement from "../pages/Branch/BranchManagement";
import BranchDetailsPage from "../pages/Branch/BranchDetailsPage";
import CreateBranchPage from "../pages/Branch/CreateBranchPage";
import BranchHierarchyPage from "../pages/Branch/BranchHierarchyPage";

import { PRODUCT_ROUTES, PRODUCT_PERMISSIONS } from "./productRoutes";
import { NOTIFICATION_ROUTES } from "../constants/notification.constant";
import { JEWELLERY_ROUTES } from "../constants/jewellery.constant";

import ProductRoutes from "components/products/ProductRoutes";
import CompanyDetailsModal from "components/UI/CompanyDetailsModal";
import AppSettingsRoutes from "components/appSettings/AppSettingsRoutes";
import AdminCartRoutes from "pages/Cart/CartRoutes";
import AvailabilityManagement from "pages/Admin/AvailabilityManagement";
import Profile from "pages/Profile";
import {
  CustomerAppointmentRoutes,
  AdminAppointmentRoutes,
} from "pages/Appointments/appointmentRoutes";
import ConsultationRoutes from "pages/Consultation/consultationRoutes.js";
import OrderRoutes from "pages/Orders/OrderRoutes";
import cancellationRoutes from "pages/JewelleryPlans/Cancellation/CancellationRoutes";
import JewelleryPlanRoutes from "pages/JewelleryPlans/jewelleryPlanRoutes";
import AdminPricingRoutes from "pages/PricingRules/pricingRoutes";
import KarigarManagementRoutes from "pages/KarigarManagement/karigarManagementRoutes";

const productRoutes = [
  {
    path: "/products/*",
    element: <ProductRoutes />,
  },
];

const appSettingsRoutes = [
  {
    path: "/app-settings/*",
    element: <AppSettingsRoutes />,
  },
];

const appointmentRoutes = [
  { path: "/appointments/*", element: <AdminAppointmentRoutes /> },
];

const adminCartRoutes = [
  {
    path: "/cart/*",
    element: <AdminCartRoutes />,
  },
];

const consultationRoutes = [
  {
    path: "/consultation/*",
    element: <ConsultationRoutes />,
  },
];

const jewelleryPlanRoutes = [
  {
    path: "/jewellery/*",
    element: <JewelleryPlanRoutes />,
  },
];

const orderRoutes = [{ path: "/orders/*", element: <OrderRoutes /> }];

const priceRulesRoutes = [
  { path: "/pricing-rules/*", element: <AdminPricingRoutes /> },
];

const karigarManagementAllRoutes = [
  { path: "/karigar-management/*", element: <KarigarManagementRoutes /> },
];

export const BRANCH_ROUTES = {
  // Main routes
  LIST: "/branches",
  CREATE: "/branches/create",
  DETAILS: "/branches/:branchId",
  EDIT: "/branches/:branchId/edit",
  HIERARCHY: "/branches/hierarchy",

  // Store routes
  STORES: "/branches/:branchId/stores",
  CREATE_STORE: "/branches/:branchId/stores/create",
  STORE_DETAILS: "/branches/:branchId/stores/:storeId",

  // Configuration routes
  RATE_CONFIG: "/branches/:branchId/rate-config",
  WORKING_HOURS: "/branches/:branchId/working-hours",

  // Analytics routes
  ANALYTICS: "/branches/:branchId/analytics",
  COMPARISON: "/branches/comparison",

  // Bulk operations
  BULK_UPDATE: "/branches/bulk-update",
};

export const RATE_ROUTES = {
  ROOT: "/rates",
  DASHBOARD: "/rates/dashboard",
  DAILY: "/rates/daily",
  BRANCH_CONFIG: "/rates/branch-config",
  STONES: "/rates/stones",
  ANALYTICS: "/rates/analytics",
  CHARGES: "/rates/charges",
};

// Route paths constants
export const ROUTE_PATHS = {
  LOGIN: "/login",
  INITIAL_SETUP: "/initial-setup",
  HOME: "/",
  ORDERS: "/orders-old",
  CATEGORY: "/category",
  PRODUCT: "/products",
  USER: "/customers",
  ADMIN: "/admin",
  PROFILE: "/profile",
  NOT_FOUND: "*",
  PRIVACY_POLICY: "/public/privacy-policy",
  USER_REQUESTS: "/customer-requests",
  USAGE: "/usage",
  COMPANY_DETAILS: "/company-details",
};

// Main routes configuration
export const routes = [
  {
    path: ROUTE_PATHS.LOGIN,
    element: <Login />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <Layout />,
        children: [
          // Dashboard
          { path: ROUTE_PATHS.HOME, element: <OrderRoutes /> },

          // Operations
          // { path: ROUTE_PATHS.ORDERS, element: <Orders /> },
          { path: ROUTE_PATHS.USER_REQUESTS, element: <UserRequestsPage /> },

          // Catalog Management
          // { path: ROUTE_PATHS.PRODUCT, element: <AdminProductPage /> },
          { path: ROUTE_PATHS.CATEGORY, element: <CategoryPage /> },

          // User Management
          { path: ROUTE_PATHS.USER, element: <UsersPage /> },
          { path: ROUTE_PATHS.PROFILE, element: <Profile /> },
          { path: ROUTE_PATHS.ADMIN, element: <AdminPage /> },

          // Analytics
          { path: ROUTE_PATHS.USAGE, element: <UsageDashboard /> },

          // Notifications
          {
            path: NOTIFICATION_ROUTES.ROOT,
            element: <NotificationsDashboard />,
          },
          { path: NOTIFICATION_ROUTES.CAMPAIGNS, element: <CampaignWizard /> },
          { path: NOTIFICATION_ROUTES.JOBS, element: <JobsPage /> },
          { path: NOTIFICATION_ROUTES.DEVICES, element: <DeviceRegistry /> },
          { path: NOTIFICATION_ROUTES.ANALYTICS, element: <AnalyticsPage /> },
          { path: NOTIFICATION_ROUTES.TEMPLATES, element: <TemplateManager /> },

          // Settings
          {
            path: ROUTE_PATHS.COMPANY_DETAILS,
            element: <CompanyDetailsModal isPage={true} />,
          },

          // ==================== JEWELLERY MODULE ROUTES ====================

          {
            path: RATE_ROUTES.ROOT,
            element: <RateDashboard />,
          },
          {
            path: RATE_ROUTES.DAILY,
            element: <DailyRateManager />,
          },
          {
            path: RATE_ROUTES.BRANCH_CONFIG,
            element: <BranchRateConfig />,
          },
          {
            path: RATE_ROUTES.STONES,
            element: <StoneRateMatrix />,
          },
          {
            path: RATE_ROUTES.ANALYTICS,
            element: <RateAnalytics />,
          },
          {
            path: RATE_ROUTES.CHARGES,
            element: <ChargesManager />,
          },
          {
            path: BRANCH_ROUTES.LIST,
            element: <BranchManagement />,
          },
          {
            path: BRANCH_ROUTES.CREATE,
            element: <CreateBranchPage />,
          },
          {
            path: BRANCH_ROUTES.DETAILS,
            element: <BranchDetailsPage />,
          },
          {
            path: BRANCH_ROUTES.HIERARCHY,
            element: <BranchHierarchyPage />,
          },
          {
            path: "/my-availability",
            element: <AvailabilityManagement />,
          },
          ...productRoutes,
          ...appSettingsRoutes,
          ...adminCartRoutes,
          ...appointmentRoutes,
          ...consultationRoutes,
          ...orderRoutes,
          ...priceRulesRoutes,
          ...jewelleryPlanRoutes,
          ...karigarManagementAllRoutes,
        ],
      },
      {
        path: ROUTE_PATHS.INITIAL_SETUP,
        element: <InitialSetupWizard />,
      },
    ],
  },
  {
    path: ROUTE_PATHS.NOT_FOUND,
    element: <NotFound />,
  },
  {
    path: ROUTE_PATHS.PRIVACY_POLICY,
    element: <PrivacyPolicy />,
  },
];

export { menuConfig as menuRoutes, getFlatMenuItems } from "./menuConfig";
